import { useCallback, useMemo, useState } from 'react'

import { FilterOptionsState } from '@mui/material'

import { produce } from 'immer'

import { SELECT_OPTION } from '../constants'
import { AutocompleteOption, BulkTagValueExact, BulkTagsFieldValue } from '../types'

// Rebuild the field value from a new autocomplete selection.
// Selected options become `exact`, while previously-`exact` tags that are no
// longer selected are marked `deleted` so the bulk-edit diff sends an explicit
// removal to the server. `partial` and existing `deleted` tags are preserved.
function applySelection(
  value: BulkTagsFieldValue,
  selectedOptions: AutocompleteOption[],
): BulkTagsFieldValue {
  const selectedNames = new Set(
    selectedOptions.filter(opt => opt.type !== 'button').map(opt => opt.value),
  )

  return produce(value, draft => {
    for (const [name, tag] of Object.entries(draft.tags)) {
      if (tag.status === 'exact' && !selectedNames.has(name)) {
        draft.tags[name] = { name, status: 'deleted' }
      }
    }

    selectedOptions
      .filter(opt => opt.type !== 'button')
      .forEach(opt => {
        draft.tags[opt.value] = {
          name: opt.value,
          status: 'exact',
          warning: opt.warning,
        }
      })
  })
}

export function useAutocompleteManageProps(
  onChange: (values: BulkTagsFieldValue) => void,
  value: BulkTagsFieldValue,
  options: BulkTagValueExact[],
  totalSelectedItems: number,
) {
  // manage the input value of the autocomplete
  const [inputValue, setInputValue] = useState<string>('')

  const selectedOptions: AutocompleteOption[] = useMemo(() => {
    return Object.values(options).map(tag => ({
      label: tag.name,
      value: tag.name,
      count: 0,
      allocationCount: totalSelectedItems,
      warning: tag.warning,
      type: 'option',
    }))
  }, [options, totalSelectedItems])

  const getOptionLabel = (option: AutocompleteOption) => option.label

  const getOptionDisabled = (option: AutocompleteOption) => !!option.warning

  const isOptionEqualToValue = (option: AutocompleteOption, value: AutocompleteOption) =>
    option.value === value.value

  const filterOptions = (
    filtered: AutocompleteOption[],
    state: FilterOptionsState<AutocompleteOption>,
  ) => {
    const inputValue = state.inputValue?.trim().toLowerCase() || ''
    const finalFiltered = filtered.filter(option => option.label.toLowerCase().includes(inputValue))
    //Add the select option to the filtered options
    return [...finalFiltered, SELECT_OPTION]
  }

  const onChangeValue = (_: any, newValue: AutocompleteOption[]) => {
    onChange(applySelection(value, newValue))
  }

  const onChangeInput = (_, newInputValue: string) => {
    setInputValue(newInputValue)
  }

  const onClearInput = () => {
    setInputValue('')
  }

  const onSelectDeselectAllOptions = useCallback(
    (allOptionsSelected: boolean, selectableFilteredOptions: AutocompleteOption[]) => {
      //If all options are selected, deselect them, otherwise select all
      const updated = allOptionsSelected
        ? selectedOptions.filter(
            selected =>
              !selectableFilteredOptions.some(filtered => filtered.value === selected.value),
          )
        : [
            ...selectedOptions,
            ...selectableFilteredOptions.filter(
              option => !selectedOptions.some(selected => selected.value === option.value),
            ),
          ]
      onChange(applySelection(value, updated))
    },
    [onChange, selectedOptions, value],
  )

  return {
    inputValue,
    selectedOptions,
    isOptionEqualToValue,
    getOptionLabel,
    getOptionDisabled,
    filterOptions,
    onChangeValue,
    onChangeInput,
    onClearInput,
    onSelectDeselectAllOptions,
  }
}
